import type {
  LockerverseAuctionItem,
  LockerverseAuctionPricing,
} from "@lockerverse/sdk/auctions";
import { AddressElement, PaymentElement } from "@stripe/react-stripe-js";
import type {
  StripeAddressElementChangeEvent,
  StripePaymentElementOptions,
} from "@stripe/stripe-js";
import { ArrowRight, ChevronLeft } from "lucide-react";
import type { FormEventHandler } from "react";
import { formatAuctionMoney as formatMoney } from "./auction-display.ts";
import { Checkbox, EmailField, FormField } from "./form-field.tsx";
import type { PhoneInputValue } from "./phone-field.tsx";
import { PhoneInput } from "./phone-field.tsx";

type AuctionCheckoutViewProps = {
  buttonText: string;
  communityName: string;
  email: string;
  contactErrors: { email?: string; phone?: string };
  phoneCountry: string;
  id: string;
  includeServiceFee: boolean;
  isPurchase: boolean;
  isTerminal: boolean;
  item: LockerverseAuctionItem;
  locked: boolean;
  message: string | null;
  onBack?: (() => void) | undefined;
  onBillingChange: (event: StripeAddressElementChangeEvent) => void;
  onEmailChange: (value: string) => void;
  onIncludeServiceFeeChange?: ((value: boolean) => void) | undefined;
  onPaymentLoadError: () => void;
  onPhoneChange: (value: PhoneInputValue) => void;
  onSameAsBillingChange: (value: boolean) => void;
  onShippingChange: (event: StripeAddressElementChangeEvent) => void;
  onSubmit: FormEventHandler<HTMLFormElement>;
  phone: string;
  pricing: LockerverseAuctionPricing;
  quantity: number;
  sameAsBilling: boolean;
  serviceFeeCents: number;
  serviceFeeEnabled: boolean;
  showFields: boolean;
  submitDisabled: boolean;
};

function getPaymentOptions(isPurchase: boolean): StripePaymentElementOptions {
  return {
    business: { name: "Lockerverse" },
    fields: {
      billingDetails: {
        address: "never",
        email: "never",
        name: "never",
        phone: "never",
      },
    },
    terms: {
      applePay: isPurchase ? "auto" : "never",
      auBecsDebit: "never",
      bancontact: "never",
      card: "always",
      cashapp: "never",
      googlePay: "never",
      ideal: "never",
      paypal: isPurchase ? "auto" : "never",
      sepaDebit: "never",
      sofort: "never",
      usBankAccount: "never",
    },
    wallets: { applePay: isPurchase ? "auto" : "never", googlePay: "never" },
  };
}

export function AuctionCheckoutView(props: AuctionCheckoutViewProps) {
  const { isPurchase, item, pricing, id } = props;
  return (
    <form
      className="lockerverse-auctions__checkout"
      noValidate
      onSubmit={props.onSubmit}
    >
      <div className="lockerverse-auctions__checkout-header">
        <div className="lockerverse-auctions__checkout-navigation">
          <button
            aria-label="Back"
            className="lockerverse-auctions__checkout-back"
            disabled={!props.onBack}
            onClick={props.onBack}
            type="button"
          >
            <ChevronLeft aria-hidden="true" size={20} />
          </button>
          <div>
            <p className="lockerverse-auctions__checkout-eyebrow">
              Step 2 of 2
            </p>
            <p>{isPurchase ? "Secure checkout" : "Secure payment method"}</p>
          </div>
        </div>
        <p className="lockerverse-auctions__checkout-eyebrow">
          {props.communityName}
        </p>
        <h2>
          {isPurchase
            ? "Finalize your purchase."
            : "Finalize your bid authorization."}
        </h2>
        <p className="lockerverse-auctions__checkout-summary">
          {isPurchase ? (
            <>
              Pay <strong>{formatMoney(pricing.totalCents)}</strong> for{" "}
              {props.quantity} {props.quantity === 1 ? "item" : "items"} of{" "}
              <strong>{item.name}</strong>. Your card is charged immediately
              once checkout succeeds.
            </>
          ) : (
            <>
              Authorize a payment method for your bid of{" "}
              <strong>{formatMoney(pricing.subtotalCents)}</strong> on{" "}
              <strong>{item.name}</strong>. Your card is only charged if this
              bid wins the item.
            </>
          )}
        </p>
        {item.collectShippingAddress && (
          <div className="lockerverse-auctions__checkout-physical">
            <p className="lockerverse-auctions__checkout-eyebrow">
              Physical item
            </p>
            <p>
              This item requires a delivery address. Billing details are used by
              default, and you can switch to a separate delivery address below
              if needed.
            </p>
          </div>
        )}
      </div>
      <div className="lockerverse-auctions__checkout-body">
        {props.showFields && (
          <fieldset disabled={props.locked}>
            <div className="lockerverse-auctions__checkout-contact">
              <EmailField
                error={props.contactErrors.email}
                id={`${id}-email`}
                label="Email"
                name="email"
                onChange={(event) => props.onEmailChange(event.target.value)}
                required
                value={props.email}
              />
              <FormField
                error={props.contactErrors.phone}
                id={`${id}-phone`}
                label="Phone"
              >
                <PhoneInput
                  country={props.phoneCountry}
                  error={props.contactErrors.phone}
                  errorId={`${id}-phone-error`}
                  id={`${id}-phone`}
                  name="phone"
                  onChange={props.onPhoneChange}
                  required
                  value={props.phone}
                />
              </FormField>
            </div>
            <section
              aria-label="Payment Information"
              className="lockerverse-auctions__checkout-section"
            >
              <div className="lockerverse-auctions__checkout-section-heading">
                <h3>Payment Information</h3>
                <p>
                  {isPurchase
                    ? "Processed securely and charged immediately for this purchase."
                    : "Stored securely and charged only if your bid finishes on top."}
                </p>
              </div>
              <PaymentElement
                onLoadError={props.onPaymentLoadError}
                options={getPaymentOptions(isPurchase)}
              />
            </section>
            <section
              aria-label="Billing Details"
              className="lockerverse-auctions__checkout-section"
            >
              <h3>Billing Details</h3>
              <AddressElement
                onChange={props.onBillingChange}
                options={{ mode: "billing" }}
              />
              <p>
                {isPurchase
                  ? "Use your full name here. We use the email and phone above for purchase confirmation and fulfillment updates."
                  : "Use the bidder's full name here. We use the email and phone above to contact you if this bid is outbid or wins."}
              </p>
            </section>
            {item.collectShippingAddress && (
              <section
                aria-label="Delivery Details"
                className="lockerverse-auctions__checkout-section"
              >
                <label
                  className="lockerverse-auctions__checkout-checkbox lockerverse-auctions__checkout-delivery"
                  htmlFor={`${id}-delivery-same-as-billing`}
                >
                  <Checkbox
                    checked={props.sameAsBilling}
                    id={`${id}-delivery-same-as-billing`}
                    onChange={(event) =>
                      props.onSameAsBillingChange(event.target.checked)
                    }
                  />
                  <span>
                    <strong>Delivery address is the same as billing</strong>
                    <span>
                      Leave this on for the fastest flow. Turn it off only if
                      the delivery address should differ from the billing
                      details above.
                    </span>
                  </span>
                </label>
                {!props.sameAsBilling && (
                  <>
                    <h3>Delivery Address</h3>
                    <AddressElement
                      onChange={props.onShippingChange}
                      options={{ mode: "shipping" }}
                    />
                  </>
                )}
              </section>
            )}
            {props.serviceFeeEnabled && <CheckoutServiceFee {...props} />}
          </fieldset>
        )}
        {props.message && (
          <p
            aria-live="polite"
            className="lockerverse-auctions__notice"
            role="status"
          >
            {props.message}
          </p>
        )}
        {!props.isTerminal && (
          <div className="lockerverse-auctions__checkout-footer">
            <p>
              {isPurchase
                ? "By continuing, you agree to be charged for this purchase."
                : "By continuing, you agree to have this payment method stored for item settlement if your bid is the winning offer."}
              {item.collectShippingAddress
                ? ` Your delivery details are stored with the ${isPurchase ? "order" : "bid"} so the seller can fulfill the item after payment.`
                : ""}
            </p>
            <button
              className="lockerverse-auctions__button lockerverse-auctions__checkout-submit"
              disabled={props.submitDisabled}
              type="submit"
            >
              {props.buttonText}
              <ArrowRight aria-hidden="true" size={16} />
            </button>
          </div>
        )}
      </div>
    </form>
  );
}

function CheckoutServiceFee(props: AuctionCheckoutViewProps) {
  const { isPurchase, pricing } = props;
  return (
    <section
      aria-label="Processing fees"
      className="lockerverse-auctions__checkout-section"
    >
      {props.onIncludeServiceFeeChange ? (
        <label
          className="lockerverse-auctions__checkout-checkbox"
          htmlFor={`${props.id}-service-fee`}
        >
          <Checkbox
            checked={props.includeServiceFee}
            id={`${props.id}-service-fee`}
            onChange={(event) =>
              props.onIncludeServiceFeeChange?.(event.target.checked)
            }
          />
          <span>
            <strong>
              {isPurchase ? "Add" : "If I win, add"}{" "}
              {formatMoney(props.serviceFeeCents)} to help cover processing fees
            </strong>
            <span>
              {isPurchase ? "Total" : "Total if I win"}:{" "}
              {formatMoney(pricing.totalCents)}
            </span>
          </span>
        </label>
      ) : (
        <p>
          {isPurchase ? "Total" : "Total if I win"}:{" "}
          {formatMoney(pricing.totalCents)}
        </p>
      )}
    </section>
  );
}
